/**
 * useCreateBookingWorkshops — the DATA half of the create-a-booking workshop
 * picker (BON-142, lifted out of `WorkshopPicker` in BON-146 so one picker
 * component can serve two sources).
 *
 * Owns the region + tree filters, the month-scoped fetch, and the hold-aware
 * availability overlay. Returns workshops already normalized onto
 * {@link PickerWorkshop} and already tree-filtered — the picker itself never
 * sees a `Workshop`.
 */
import { addDays, endOfMonth, format, max as maxDate, startOfMonth, subDays } from 'date-fns';
import { useEffect, useMemo, useState } from 'react';

import { fetchBookableAvailability } from '../../../api/bookingsV2';
import { fetchWorkshops, type Workshop } from '../../../api/workshops';

import { fromWorkshop, type PickerWorkshop } from './pickerWorkshop';
import { useWritableRegions } from './useWritableRegions';
import { deriveTreeOptions, isPickableWorkshop } from './WorkshopPickerHelpers';

/** Cap on the fetched page — plenty for the picker's browse use case (the
 *  backend clamps `limit` to 500 server-side). */
const FETCH_LIMIT = 500;

/** Overlay hold-aware bookable availability (BON-142) onto the fetched
 *  workshops: the list's `available` is confirmed-only, so subtract active
 *  seat-holds to get what can actually be booked. Fail-soft — if the
 *  availability read errors, the list's confirmed-only figure is kept unchanged so the
 *  picker still works (the authoritative capacity guard is the write path).
 *
 *  The rebook listing needs no equivalent: its `seatsLeft` is hold-aware on
 *  the wire. */
async function withHoldAwareAvailability(workshops: Workshop[]): Promise<Workshop[]> {
  if (workshops.length === 0) return workshops;
  try {
    const map = await fetchBookableAvailability(workshops.map((w) => w.id));
    return workshops.map((w) => (map[w.id] ? { ...w, available: map[w.id].available } : w));
  } catch {
    return workshops;
  }
}

/** The date range to fetch for a displayed month: the month itself, padded a
 *  week each side so the calendar grid's leading/trailing spillover days (from
 *  the adjacent months) still populate. Floored at the start of TODAY — today's
 *  workshops are still bookable (a CSR can book a walk-in to an in-progress
 *  one; ended ones show disabled), but fully-past days needn't load. */
export function monthFetchRange(month: Date): { from: string; to: string } {
  const gridStart = subDays(startOfMonth(month), 7);
  const gridEnd = addDays(endOfMonth(month), 7);
  const from = maxDate([gridStart, new Date()]);
  return { from: format(from, 'yyyy-MM-dd'), to: format(gridEnd, 'yyyy-MM-dd') };
}

/** Fetch + region-filter the bookable workshops for the VISIBLE month only:
 *  scoping the fetch to the shown month keeps the payload — and the hold-aware
 *  overlay's id set — small, instead of pulling all 500+ upcoming. Re-fetches
 *  on region OR month change; tree filtering happens client-side. */
function useMonthWorkshops(regionId: string | null, month: Date, enabled: boolean) {
  const [workshops, setWorkshops] = useState<PickerWorkshop[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const { from, to } = useMemo(() => monthFetchRange(month), [month]);

  useEffect(() => {
    const controller = new AbortController();
    // Held back until the viewer's writable scope is resolved. Without this the
    // first request goes out unscoped, so a confined viewer briefly fetches —
    // and could briefly render — workshops in regions they cannot book.
    if (!enabled) return undefined;
    setLoading(true);
    setError(false);
    fetchWorkshops({ regionId: regionId ?? undefined, from, to, limit: FETCH_LIMIT })
      .then(async (all) => {
        if (controller.signal.aborted) return;
        const merged = await withHoldAwareAvailability(all.filter(isPickableWorkshop));
        if (controller.signal.aborted) return;
        setWorkshops(merged.map(fromWorkshop));
      })
      .catch(() => {
        if (!controller.signal.aborted) setError(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [regionId, from, to, enabled]);

  return { workshops, loading, error };
}

export function useCreateBookingWorkshops(month: Date) {
  const [regionId, setRegionId] = useState<string | null>(null);
  const [treeSpeciesId, setTreeSpeciesId] = useState<string | null>(null);
  const { regions, confined } = useWritableRegions();

  // A confined viewer starts inside their own region rather than at "all
  // regions", which for them would fetch workshops they cannot book.
  useEffect(() => {
    if (confined && regionId === null && regions.length > 0) setRegionId(regions[0].id);
  }, [confined, regionId, regions]);

  // A confined viewer must not fetch until their region is chosen; everyone else
  // starts immediately at "all regions".
  const scopeResolved = !confined || regionId !== null;
  const { workshops, loading, error } = useMonthWorkshops(regionId, month, scopeResolved);

  const treeOptions = useMemo(() => deriveTreeOptions(workshops), [workshops]);
  const filtered = useMemo(
    () => (treeSpeciesId ? workshops.filter((w) => w.treeSpeciesId === treeSpeciesId) : workshops),
    [workshops, treeSpeciesId],
  );

  return {
    regions,
    confined,
    regionId,
    setRegionId,
    treeOptions,
    treeSpeciesId,
    setTreeSpeciesId,
    workshops: filtered,
    loading,
    error,
  };
}
